import { emptyFragment } from '@execonline-inc/execonline-ui';
import { whenGt } from '@execonline-inc/numbers';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import * as React from 'react';
import ProgramStore from '../../../../../ProgramStore';
import { CoachingProductDetails } from '../../../../../ProgramStore/Types';
import { L, T } from '../../../../../Translations';
import { ProgramCardPanel } from '../../../../Dashboard/ProgramCard';
import ModalStore from '../../../../Modal/Store';
import CoachAvatar from './CoachAvatar';

interface Props {
  programStore: ProgramStore;
  coachingProductDetails: CoachingProductDetails;
  modalStore: ModalStore;
}

// The days left turn urgent in the last week, the same as the module due badge.
const URGENT_DAYS = 7;

// Replaces the separate Your coach, Session history and Access days remaining
// boxes with one summary next to the coach's photo.
const CoachingSummary: React.FC<Props> = ({ programStore, coachingProductDetails, modalStore }) => {
  const { selectedCoach, pastSessionsCount } = coachingProductDetails;

  return (
    <ProgramCardPanel>
      <CoachAvatar coach={selectedCoach} />
      <div className="min-w-0">
        {selectedCoach
          .map((coach) => (
            <p className="m-0 text-lg font-semibold text-gray-900" data-test-coach-name={true}>
              <T kind="Sessions with {{coachName}}" coachName={coach.payload.name} />
            </p>
          ))
          .getOrElse(emptyFragment)}
        <div className="flex flex-wrap items-center gap-x-3 text-gray-600">
          <span data-test-completed-sessions={pastSessionsCount}>
            <T kind="{{count}} session completed" count={pastSessionsCount} />
          </span>
          {selectedCoach
            .map(() => (
              <button
                type="button"
                className="m-0 border-0 bg-transparent p-0 font-medium tracking-normal text-primary hover:underline"
                onClick={modalStore.open}
                data-intercom-target="dashboard-coaching-view-profile-button"
              >
                <T kind="view profile" />
              </button>
            ))
            .getOrElse(emptyFragment)}
        </div>
        {programStore.remainingDays
          .andThen(whenGt(0))
          .andThen((remainingDays) =>
            programStore.programEndsOn.map((endsOn) => ({ remainingDays, endsOn })),
          )
          .map(({ remainingDays, endsOn }) => (
            <div
              className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
              data-test-coaching-access-until={true}
            >
              <span className="text-gray-500">
                <T
                  kind="Schedule sessions until <date/>"
                  date={<L localizeable={endsOn} format="long-month-day-year" />}
                />
              </span>
              {/* Styled like the module due badge, with the urgent colors in the last week. */}
              <span
                className={clsx(
                  'rounded-sm px-2 py-0.5 text-sm font-semibold',
                  remainingDays <= URGENT_DAYS
                    ? 'bg-mistyRose text-carnelian'
                    : 'bg-gray-100 text-gray-700',
                )}
                data-test-coaching-days-remaining={remainingDays}
              >
                <T kind="{{count}} day left" count={remainingDays} />
              </span>
            </div>
          ))
          .getOrElse(() => (
            <p className="m-0 text-sm text-carnelian" data-test-coaching-license-required={true}>
              <T kind="You must have an active license to schedule sessions with your coach" />
            </p>
          ))}
      </div>
    </ProgramCardPanel>
  );
};

export default observer(CoachingSummary);
